// src/components/MainComponent.jsx
import React, { useState, useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import FTPComponent from "./itemListPlugin";
import FTPComponentSite from "./itemsListSite";
import CVEComponent from "./cveList";
import SearchBar from "./searchBar";
import useFetchAllData from "../utils/customHookAPIAllData";
import useSortableData from "../utils/customHookSortTable";
import NextMaintenanceButton from "./NextMaintenanceButton";
import useFetchMaintenance from "../utils/customHookAPIMaintenance";
import FTPComponentThemes from "./itemListTheme";
import VersionCard from "./versionCard";
import TotalCard from "./TotalCard";
import "../style/mainBox.scss";
import "../style/maintenance.scss";
import "../style/btn.scss";
import MaintenancesCard from "./MaintenancesCard";
import ClientsSitesCard from "./clientsSitesCard";
import { useFilteredForTable } from "../utils/customCVECache";
import useCacheRefresh from "../utils/customHookRefrehData";
import useFetchVersions from "../utils/customHookVersions";
import PHPDrawer from "./PHPDrawer";
import CVECard from "./CVECard";
import { compareVersions } from "../utils/cveVersionUtil";
import { Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, Chip, Divider, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import WarningIcon from "@mui/icons-material/Warning";
import CacheRefreshButton from "./cacheRefreshBtn";


const MainComponent = ({ role, isAdmin }) => {
  // Récupération de TOUTES les données en un seul appel via le nouveau hook
  const { fileContent, pluginData, themeData, isLoading, error, refreshData, updatePluginPayant } = useFetchAllData();
  const { sortedData, handleSort } = useSortableData(fileContent);
  const { maintenanceData, refreshData: refreshMaintenanceData } =
    useFetchMaintenance(role);

  const [showSiteTable, setShowSiteTable] = useState(true);
  const [showPluginTable, setShowPluginTable] = useState(false);
  const [showThemeTable, setShowThemeTable] = useState(false);
  const [showCVETable, setShowCVETable] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [phpDrawerOpen, setPhpDrawerOpen] = useState(false);
  const {phpVersion} = useFetchVersions();
  const [isPopupOpen, setPopupOpen] = useState(false);
  const { loading, handleRefreshData } = useCacheRefresh(refreshData);
    
    const handleSiteButtonClick = () => {
    setShowSiteTable(true);
    setShowPluginTable(false);
    setShowThemeTable(false);
    setShowCVETable(false);
  };

  const handlePluginButtonClick = () => {
    setShowSiteTable(false);
    setShowThemeTable(false);
    setShowPluginTable(true);
    setShowCVETable(false);
  };

  const handleThemeButtonClick = () => {
    setShowSiteTable(false);
    setShowPluginTable(false);
    setShowThemeTable(true);
    setShowCVETable(false);
  };

  const handleCVEButtonClick = () => {
    setShowSiteTable(false);
    setShowPluginTable(false);
    setShowThemeTable(false);
    setShowCVETable(true);
  };

  const handleSearch = (term) => {
    if (term.length >= 3) {
      setSearchTerm(term);
    } else {
      setSearchTerm("");
    }
  };

  const handlePhpDrawerOpen = () => {
    setPhpDrawerOpen(true);
  };

  const handlePhpDrawerClose = () => {
    setPhpDrawerOpen(false);
  };

  // Mémoïsés : ces tableaux alimentent useFilteredForTable (croisement plugins x CVE),
  // qui serait sinon recalculé intégralement à chaque render.
  const filteredData = useMemo(
    () =>
      sortedData.filter((item) =>
        Object.values(item.content)
          .join(" ")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      ),
    [sortedData, searchTerm]
  );

  const filteredPlugins = useMemo(
    () =>
      pluginData.filter((item) =>
        item.plugins.some((plugin) =>
          plugin.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      ),
    [pluginData, searchTerm]
  );

  const { filteredForTable } = useFilteredForTable(filteredPlugins);

  // On applique uniquement le filtre de recherche local.
  // Mémoïsé : une nouvelle identité de tableau re-rendait FTPComponentThemes pour rien.
  const filteredThemesBySite = useMemo(
    () =>
      themeData
        .map(themeGroup => {
          const filteredThemes = themeGroup.themes
            .filter(
              theme =>
                theme.sites.length > 0 &&
                theme.name.toLowerCase().includes(searchTerm.toLowerCase())
            );

          return filteredThemes.length > 0 ? { themes: filteredThemes } : null;
        })
        .filter(themeGroup => themeGroup !== null),
    [themeData, searchTerm]
  );

  // Pour la deuxième carte de total
  const ONETSites = useMemo(
    () =>
      filteredData.filter(
        (item) =>
          (item.content.name.toLowerCase().includes("onet") ||
            item.content["selected-service"].toLowerCase().includes("onet")) &&
          item.content.environnement === "PRD"
      ),
    [filteredData]
  );

  const compassSites = useMemo(
    () =>
      filteredData.filter(
        (item) =>
          (item.content.name.toLowerCase().includes("compass") ||
            item.content["selected-service"].toLowerCase().includes("compass")) &&
          item.content.environnement === "PRD"
      ),
    [filteredData]
  );

      // Mémoïsé : sinon recalculé intégralement à chaque frappe dans la recherche.
      const { totalCVE, criticalCVEList } = useMemo(() => {
        let total = 0;
        const critical = [];

        filteredForTable.forEach((group) => {
          (group.plugins || []).forEach((plugin) => {
            const impacted = plugin.impactedCves || [];
            total += impacted.length;

            impacted.forEach((cve) => {
              if (cve.severity == null || cve.severity < 7.0) return;

              // Les sites PRD dont la version installée est antérieure à la version corrigée.
              const affectedSites = (plugin.sites || []).filter((site) => {
                const version = site?.versionplugins;
                const cveMax = cve.maxversion;
                if (!version || !cveMax) return false;
                if (site?.env?.toUpperCase() !== "PRD") return false;

                return compareVersions(
                  String(version).trim().replace(/^v/i, ""),
                  String(cveMax).trim().replace(/^v/i, "")
                ) === -1;
              });

              if (!affectedSites.length) return;

              critical.push({
                cveId: cve.cve_id,
                severity: cve.severity,
                pluginName: plugin.name,
                maxVersion: cve.maxversion,
                affectedSitesCount: affectedSites.length,
                affectedSites: affectedSites.map((s) => {
                  const env = s?.env ? String(s.env).trim().toUpperCase() : null;
                  return env ? `${s.site} (${env})` : s.site;
                }),
              });
            });
          });
        });

        // Trié ici : le JSX le refaisait à chaque render, en mutant le tableau.
        critical.sort((a, b) => b.severity - a.severity);

        return { totalCVE: total, criticalCVEList: critical };
      }, [filteredForTable]);

      const criticalCVE = criticalCVEList.length;

      useEffect(() => {
      var lastShownTs = +localStorage.getItem("lastShown");
      var currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      var lastShown = null;
      if (!isNaN(lastShownTs)) {
        lastShown = new Date(lastShownTs);
        lastShown.setHours(0, 0, 0, 0);
      }
      if ((lastShown == null || lastShown.getTime() != currentDate.getTime()) && (criticalCVE != null && criticalCVE>0)) {
        setPopupOpen(true);
        localStorage.setItem("lastShown", currentDate.getTime());
      }
    }, [criticalCVE]);

      // Mémoïsé : deux regex par site, sinon rejouées à chaque render.
      const obsoletePHP = useMemo(
        () =>
          filteredData.filter((item) => {
            if (!item.content.php_version || !phpVersion) return false;

            const currentPHP = item.content.php_version;
            const latestPHP = phpVersion;

            // Vérifier que les deux sont des strings
            if (typeof currentPHP !== 'string' || typeof latestPHP !== 'string') return false;

            const currentMatch = currentPHP.match(/^(\d+)\.(\d+)/);
            const latestMatch = latestPHP.match(/^(\d+)\.(\d+)/);

            if (!currentMatch || !latestMatch) return false;

            const currentMajor = parseInt(currentMatch[1]);
            const currentMinor = parseInt(currentMatch[2]);
            const latestMajor = parseInt(latestMatch[1]);
            const latestMinor = parseInt(latestMatch[2]);

            if (currentMajor < latestMajor) return true;

            if (currentMajor === latestMajor && (latestMinor - currentMinor > 2)) {
              return true;
            }

            return false;
          }),
        [filteredData, phpVersion]
      );

  return (
    <Box className="mainBox" sx={{ p: 0, paddingBottom:2}}>
      <Dialog
        open={isPopupOpen}
        maxWidth="sm"
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            minHeight: '60vh',
            maxHeight: '80vh',
            borderRadius:2
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          CVE critiques non résolues ({criticalCVE})
        </DialogTitle>
        <DialogContent dividers>
          {criticalCVEList.length === 0 ? (
            <Typography color="success.main">
              Aucune CVE critique détectée en production
            </Typography>
          ) : (
            <List>
              {criticalCVEList
                .map((cve, index) => (
                  <React.Fragment key={`${cve.cveId}-${index}`}>
                    <ListItem alignItems="flex-start">
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography component="span" variant="subtitle1" fontWeight="bold">
                              {cve.cveId}
                            </Typography>
                            <Chip
                              label={`Sévérité: ${cve.severity}`}
                              color="error"
                              size="small"
                            />
                            <Chip
                              label={`${cve.affectedSitesCount} site${cve.affectedSitesCount > 1 ? 's' : ''} affecté${cve.affectedSitesCount > 1 ? 's' : ''}`}
                              color="warning"
                              size="small"
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography component="span" variant="body2" color="text.primary">
                              Plugin: {cve.pluginName}
                            </Typography>
                            <br />
                            <Typography component="span" variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              Sites: {cve.affectedSites.join(', ')}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < criticalCVEList.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPopupOpen(false)} variant="contained">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
      <Box className="mainBox__btn" sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            gap:2,
            alignItems: "center",
          }}
        >
          <TotalCard total={filteredData.length} subtitle="sites au total"/>
          <VersionCard
              logo1={"/assets/WordPress_blue_logo.svg.png"}
              logo2={"/assets/PHP-logo.svg.png"}
          />
          <MaintenancesCard
          data={filteredData}
          subtitle="maintenances"/>
        </Box>
        <Box sx={{ ml: "auto" }}>

      </Box>
    </Box>
      <Box className="mainBox__maintenance" sx={{ mb: 2, display: "flex", gap: 2, alignItems: "flex-start" }}>
        <Box sx={{ display: "flex", flexDirection: "column", mt: -1.5, justifyContent: "flex-start" }}>
          <NextMaintenanceButton data={fileContent} role={role} />
          <CacheRefreshButton role={role} onClick={handleRefreshData} loading={loading}/>
          <Box className="mainBox__search" pt={2}>
            <SearchBar onSearch={handleSearch} role={role} />
          </Box>
        </Box>

        {role === "admin" && (
          <CVECard title="Total CVE" total={totalCVE} subtitle={`dont ${criticalCVE} ${criticalCVE <= 1 ? 'critique' : 'critiques'}`} />
        )}

        {role === "admin" && (
          <TotalCard
            total={obsoletePHP.length}
            subtitle="PHP obsolètes"
            onClick={handlePhpDrawerOpen}
          />
        )}

        {role === "admin" && (
        <Box sx={{ ml: "auto", mt: "-118px"}}>
          <ClientsSitesCard 
            client1Sites={ONETSites.length} 
            client2Sites={compassSites.length} 
            subtitle1="ONET" 
            subtitle2="Compass"
          />
        </Box>)}
      </Box>
      <Box className="mainBox__table">
        {showSiteTable && (
          <>
            <FTPComponentSite
              filteredData={filteredData}
              cveList={filteredForTable}
              handleSort={handleSort}
              role={role}
              isLoading={isLoading}
              error={error}
              handlePluginButtonClick={handlePluginButtonClick}
              handleSiteButtonClick={handleSiteButtonClick}
              handleThemeButtonClick={handleThemeButtonClick}
              handleCVEButtonClick={handleCVEButtonClick}
            />
          </>
        )}
        {showPluginTable && (
          <>
            <FTPComponent
              filteredPlugins={filteredPlugins}
              handleSort={handleSort}
              role={role}
              isAdmin={isAdmin}
              updatePluginPayant={updatePluginPayant}
              searchTerm={searchTerm}
              isLoading={isLoading}
              error={error}
              handlePluginButtonClick={handlePluginButtonClick}
              handleSiteButtonClick={handleSiteButtonClick}
              handleThemeButtonClick={handleThemeButtonClick}
              handleCVEButtonClick={handleCVEButtonClick}

            />
          </>
        )}
        {showThemeTable && (
          <>
            <FTPComponentThemes
              filteredThemes={filteredThemesBySite}
              handleSort={handleSort}
              searchTerm={searchTerm}
              isLoading={isLoading}
              error={error}
              handlePluginButtonClick={handlePluginButtonClick}
              handleSiteButtonClick={handleSiteButtonClick}
              handleThemeButtonClick={handleThemeButtonClick}
              handleCVEButtonClick={handleCVEButtonClick}
              role={role}
            />
          </>
        )}
        {showCVETable && (
          <>
            <CVEComponent
              filteredForTable={filteredForTable}
              handlePluginButtonClick={handlePluginButtonClick}
              handleSiteButtonClick={handleSiteButtonClick}
              handleThemeButtonClick={handleThemeButtonClick}
              handleCVEButtonClick={handleCVEButtonClick}
              role={role}
            />
          </>
        )}
      </Box>
      <PHPDrawer
        open={phpDrawerOpen}
        obsoletePHP={obsoletePHP}
        handleCloseDrawer={handlePhpDrawerClose}
        role={role}
      />
    </Box>
  );
};

export default MainComponent;
